import { useCallback } from 'react'
import { ProductOpeningHoursResponse } from '@leid/api-locations'
import { ProductAvailabilityInfo } from '@leid/api-orders'
import { addHours, endOfHour, isBefore, isSameDay, isToday, startOfHour, subHours } from 'date-fns'

import { buildDate, isImmediateRental } from 'app/utils/dates'
import { CartFormValues, OrderInfo } from 'appTypes/orders'
import { DateTimePickerHourPredicate } from 'components/form/DateTimePicker/interfaces'
import {
  checkIfPeriodStartsEndsInWorkingHours,
  getAvailabilitiesChanges,
  getClosestUnavailableDate,
  getLocationAvailabilityInfo,
} from 'helpers/utils/availabilities'

export const useShouldDisableReturnHour = (
  values: CartFormValues,
  initialValues: CartFormValues,
  isProlonging: boolean,
  order: OrderInfo | undefined,
  productLocationAvailability: ProductAvailabilityInfo,
  openingHoursInfo: ProductOpeningHoursResponse[],
): DateTimePickerHourPredicate => {
  const calculateForProlongation = (hour: string, date: Date): boolean => {
    const now = new Date()
    if (isToday(date) && Number(hour) <= now.getHours()) {
      // If it's past - hour not available
      return true
    }

    const locationAvailability = values.selectedLocationId
      ? getLocationAvailabilityInfo(values.selectedLocationId, productLocationAvailability)
      : undefined

    const isRerent = order?.order.isRerent ?? false

    if (initialValues.endDate && initialValues.returnAt) {
      if (isSameDay(date, initialValues.endDate) && Number(hour) <= Number(initialValues.returnAt)) {
        // If it's before order end - hour not available
        return true
      }

      const rentedTo = buildDate(initialValues.endDate, initialValues.returnAt)

      // If rent ended in past - rentedTo date is actually now
      const rentedToDate = isBefore(rentedTo, now) ? now : rentedTo
      const nearestDisabledDate = getClosestUnavailableDate(rentedToDate, locationAvailability, isRerent, values.amount)
      if (nearestDisabledDate) {
        const shiftedNearestDisabledDate = addHours(nearestDisabledDate, 1)
        if (!isBefore(date, shiftedNearestDisabledDate)) {
          // If exists any disabled date in future - we should block any hours after it
          return true
        }
      }
    }

    if (!values.selectedLocationId) {
      return false
    }

    if (!locationAvailability) {
      // no info for location — not available
      return true
    }

    const start = startOfHour(date)
    const end = endOfHour(start)

    // If during this hour there were some unavailabilities - whole hour not available
    const availabilitiesChange = getAvailabilitiesChanges(start, end, locationAvailability, false)
    if (availabilitiesChange.some((s) => s < values.amount)) {
      // Hour is not available
      return true
    }

    // Check opening hours for start and end of period
    const startsOrEndsInWorkingHours = checkIfPeriodStartsEndsInWorkingHours(
      start,
      end,
      values.selectedLocationId,
      false, // Check only that it's end time
      openingHoursInfo,
      true, // Here check time
    )
    if (!startsOrEndsInWorkingHours) {
      // It's not working hours or day
      return true
    }

    return false
  }

  const calculateForRegularOrder = (hour: string, date: Date): boolean => {
    // We shift an hour to the right, to reflect the fact, that we rent till the end of an hour and 21:00 for pick up is not the same
    // as 21:00 for return dates (for return 21:00 is actually 20:59)
    const shiftedDate = subHours(date, 1)

    const locationAvailability = values.selectedLocationId
      ? getLocationAvailabilityInfo(values.selectedLocationId, productLocationAvailability)
      : undefined

    if (values.startDate) {
      const fullStartDate = buildDate(values.startDate, values.pickupAt)
      const isImmediate = isImmediateRental(fullStartDate)
      if (isSameDay(date, values.startDate)) {
        if (
          Number(hour) <= Number(values.pickupAt) ||
          (isImmediate && Number(hour) <= Number(startOfHour(new Date()).getHours()) + 1)
        ) {
          return true
        }
      }

      const isRerent = order?.order.isRerent ?? false

      const nearestDisabledDate = getClosestUnavailableDate(
        fullStartDate,
        locationAvailability,
        isRerent,
        values.amount,
      )
      if (nearestDisabledDate) {
        if (isSameDay(shiftedDate, nearestDisabledDate) && shiftedDate.getHours() > nearestDisabledDate.getHours()) {
          return true
        }
      }
    }

    if (!values.selectedLocationId) {
      return false
    }

    if (!locationAvailability) {
      // no info for location — not available
      return true
    }

    const start = startOfHour(shiftedDate)
    const end = endOfHour(start)

    const availabilitiesChange = getAvailabilitiesChanges(start, end, locationAvailability, false)
    if (availabilitiesChange.some((s) => s < values.amount)) {
      // Hour is not available
      return true
    }

    const startUnshifted = startOfHour(date)
    const endUnshifted = endOfHour(startUnshifted)
    // Check opening hours for start and end of period
    const startsOrEndsInWorkingHours = checkIfPeriodStartsEndsInWorkingHours(
      startUnshifted,
      endUnshifted,
      values.selectedLocationId,
      false, // Check only that it's end time
      openingHoursInfo,
      true, // Here check time
    )
    if (!startsOrEndsInWorkingHours) {
      // It's not working hours or day
      return true
    }

    return false
  }

  return useCallback(
    (hour, date) => {
      if (isProlonging) {
        return calculateForProlongation(hour, date)
      }

      return calculateForRegularOrder(hour, date)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isProlonging,
      values.startDate,
      values.pickupAt,
      values.selectedLocationId,
      values.selectedRegionId,
      values.amount,
      openingHoursInfo,
      productLocationAvailability,
    ],
  )
}
